/**
 * Backend API Service cho VGCA Signing
 * =====================================
 * Gọi đến Backend .NET để thực hiện luồng ký số VGCA.
 *
 * Luồng ký số 3 bước:
 *   1. prepareSign()   - Gửi PDF + chứng thư số lên Backend -> nhận sessionId + hash
 *   2. (Frontend gọi vgcaService.signHash() để ký hash qua USB Token)
 *   3. completeSign()  - Gửi chữ ký PKCS#7 lên Backend -> nhận PDF đã ký
 */

import axios from 'axios'
import Vue from 'vue'

const API_BASE = process.env.VUE_APP_API_URL

function getAuthHeader() {
  if (!Vue.prototype.$auth_token) {
    let token = JSON.parse(localStorage.getItem('user-token'))
    Vue.prototype.$auth_token = token
  }
  return { Authorization: `Bearer ${Vue.prototype.$auth_token}` }
}

const http = axios.create({
  baseURL: API_BASE + 'vgca-sign/',
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Attach auth token on every request
http.interceptors.request.use((config) => {
  Object.assign(config.headers, getAuthHeader())
  return config
})

/**
 * Bước 1: Chuẩn bị ký số
 * @param {string} pdfBase64 - Nội dung PDF (Base64)
 * @param {string} certBase64 - Chứng thư số X.509 (Base64)
 * @param {string|null} signImageBase64 - Hình ảnh chữ ký (Base64)
 * @param {Array} signatures - Danh sách chữ ký [{x, y, page, width, height, type, ...}]
 * @returns {Promise<{sessionId: string, hashBase64: string}>}
 */
export async function prepareSign(pdfBase64, certBase64, signImageBase64, signatures) {
  const payload = {
    PdfBase64: pdfBase64,
    CertBase64: certBase64,
    SignatureImageBase64: signImageBase64 || null,
    Signatures: signatures.map(sig => ({
      X: sig.x,
      Y: sig.y,
      Page: sig.page,
      Width: sig.width,
      Height: sig.height,
      SignatureImageBase64: sig.imageBase64 || null,
      Type: sig.type || 'signature',
      TextContent: sig.textContent || null,
      FontSize: sig.fontSize || 13
    }))
  }

  // Backward compatibility: flat fields from first signature
  if (signatures.length > 0) {
    const first = signatures[0]
    payload.X = first.x
    payload.Y = first.y
    payload.Page = first.page
    payload.Width = first.width
    payload.Height = first.height
  }

  const response = await http.post('prepare-sign', payload)
  const data = response.data

  if (!data.success || !data.data) {
    throw new Error(data.message || 'Backend không trả về dữ liệu.')
  }

  return {
    sessionId: data.data.sessionId,
    hashBase64: data.data.hashBase64
  }
}

/**
 * Bước 3: Hoàn tất ký số
 * @param {string} sessionId - ID phiên ký từ prepareSign()
 * @param {string} signedHashBase64 - Chữ ký PKCS#7 (Base64)
 * @returns {Promise<{fileId: string, signedPdfBase64: string, message: string}>}
 */
export async function completeSign(sessionId, signedHashBase64) {
  const response = await http.post('complete-sign', {
    SessionId: sessionId,
    SignedHashBase64: signedHashBase64
  })
  const data = response.data

  if (!data.success || !data.data) {
    throw new Error(data.message || 'Backend không trả về dữ liệu.')
  }

  return {
    fileId: data.data.fileId,
    downloadUrl: data.data.downloadUrl,
    signedPdfBase64: data.data.signedPdfBase64,
    message: data.message || 'Ký số thành công!'
  }
}

/**
 * Download file PDF đã ký
 */
export async function downloadSigned(fileId, fileName) {
  const response = await http.get('download/' + fileId, {
    responseType: 'blob'
  })

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.download = fileName || 'signed.pdf'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export default {
  prepareSign,
  completeSign,
  downloadSigned
}
